import { useState } from 'react';
import { Popover, Table, message } from 'antd';
import styled from 'styled-components';

import EditData from 'components/table/EditData';
import ChoiceMembership from 'components/table/ChoiceMembership';

import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import { ReactComponent as IconRefresh } from 'asserts/icons/ic_reflesh_alt_lined.svg';
import { ReactComponent as IconEye } from 'asserts/icons/ic_eye_alt_lined.svg';
import { ReactComponent as IconDelete } from 'asserts/icons/ic_delete.svg';

import DefaultSelectMenu from 'components/selectMenu/DefaultSelectMenu';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';
import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import modalInformationAtom from 'atom/modalInformationAtom';
import { membershipInfoColumns } from 'consts/table';
import userInforAtom from 'atom/userInfoAtom';
import { handleDate } from 'utils/date';

const MembershipTable = () => {
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo[]>([]);
  const [devicesInfo, setDevicesInfo] = useState<DevicesInfo[]>([]);

  const userInfor = useAtomValue(userInforAtom);

  const [messageApi, contextHolder] = message.useMessage();

  const setModalInformation = useSetAtom(modalInformationAtom);

  const { data, refetch } = useQuery({
    queryKey: [QueryKeys.DEVICES],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/my/membership/devices',
        params: {
          usridx: userInfor.usridx
        }
      })
  });

  useEffect(() => {
    if (userInfor.usridx !== 0) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfor.usridx]);

  useEffect(() => {
    if (data?.result === 'success') {
      setMembershipInfo(data?.membership);
      if (Array.isArray(data?.devices)) {
        setDevicesInfo(data?.devices);
      } else {
        console.error('devicesInfo is not an array');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.result]);

  const editDeviceName = useMutation({
    mutationFn: (params: any) =>
      restfetcher({
        method: 'POST',
        path: 'biz/my/membership/device/name/save',
        params: params
      })
  });

  const editTerminalId = useMutation({
    mutationFn: (params: any) =>
      restfetcher({
        method: 'POST',
        path: 'biz/my/membership/device/payment-terminal/id/save',
        params: params
      })
  });

  const editIsTerminal = useMutation({
    mutationFn: (params: any) =>
      restfetcher({
        method: 'POST',
        path: 'biz/my/membership/device/payment-terminal/save',
        params: params
      })
  });

  const editIsPrint = useMutation({
    mutationFn: (params: any) =>
      restfetcher({
        method: 'POST',
        path: 'biz/my/membership/device/printer-receipt/save',
        params: params
      })
  });

  const deleteDevice = useMutation({
    mutationFn: (params: any) =>
      restfetcher({
        method: 'POST',
        path: 'biz/my/membership/device/delete',
        params: params
      }),

    onSuccess: (data: any) => {
      if (data.result === 'success') {
        messageApi.info({
          type: 'success',
          content: '삭제되었습니다.',
          style: {
            marginTop: '10vh'
          }
        });
      }
    }
  });

  const getEditDeviceName = (index: number) => {
    const params = {
      usridx: userInfor.usridx,
      deviceidx: devicesInfo[index].deviceidx,
      devicename: devicesInfo[index].devicename
    };

    editDeviceName.mutate(params);
  };

  const handleChangeDeviceName = (newData: string, index: number) => {
    const updatedDevicesInfo = [...devicesInfo];

    updatedDevicesInfo[index].devicename = newData;
    setDevicesInfo(updatedDevicesInfo);
  };

  const getEditTerminalId = (index: number) => {
    const params = {
      usridx: userInfor.usridx,
      deviceidx: devicesInfo[index].deviceidx,
      terminalid: devicesInfo[index].terminalid
    };

    editTerminalId.mutate(params);
  };

  const handleChangeTerminalId = (newData: string, index: number) => {
    const updatedDevicesInfo = [...devicesInfo];

    updatedDevicesInfo[index].terminalid = newData;
    setDevicesInfo(updatedDevicesInfo);
  };

  const handleChangeIsTerminal = (newData: string | number, index: number) => {
    const updatedDevicesInfo = [...devicesInfo];
    updatedDevicesInfo[index].paymentterminal = newData === '1' ? 1 : 0;
    setDevicesInfo(updatedDevicesInfo);

    const params = {
      usridx: userInfor.usridx,
      deviceidx: devicesInfo[index].deviceidx,
      paymentterminal: updatedDevicesInfo[index].paymentterminal
    };

    editIsTerminal.mutate(params);
  };

  const handleChangePrintReceipt = (newData: string | number, index: number) => {
    const updatedDevicesInfo = [...devicesInfo];
    updatedDevicesInfo[index].printreceipt = newData === '1' ? 1 : 0;
    setDevicesInfo(updatedDevicesInfo);

    const params = {
      usridx: userInfor.usridx,
      deviceidx: devicesInfo[index].deviceidx,
      printreceipt: updatedDevicesInfo[index].printreceipt
    };

    editIsPrint.mutate(params);
  };

  const handleClickDeleteMemebership = (index: number) => {
    const params = {
      usridx: userInfor.usridx,
      deviceidx: devicesInfo[index].deviceidx
    };

    deleteDevice.mutate(params);
  };

  const handleClickMembershipDeleteButton = (deviceIndex: number) => {
    setModalInformation({
      type: 'DELETE_MEMBERSHIP',

      handleClickRightBtn: () => {
        handleClickDeleteMemebership(deviceIndex);
      }
    });
  };

  const dataSource = devicesInfo?.map((device, index) => {
    const {
      devicename,
      terminalid,
      loginuser,
      logindate,
      mac,
      cpu,
      mainboard,
      paymentterminal,
      printreceipt,
      membership
    } = device;

    const specContent = (
      <div>
        <p>CPU : {cpu}</p>
        <p>MAC : {mac}</p>
        <p>Mainboard : {mainboard}</p>
      </div>
    );

    const isPaymentTerminal = paymentterminal === 1 ? '보유' : '미보유';
    const isPrintReceipt = printreceipt === 1 ? '보유' : '미보유';

    const paymentTerminalOptions = [
      { label: '보유', value: '1' },
      { label: '미보유', value: '0' }
    ];

    const printReceiptOptions = [
      { label: '보유', value: '1' },
      { label: '미보유', value: '0' }
    ];

    return {
      key: index,
      name: (
        <EditData
          text={devicename}
          setNewData={(newData) => handleChangeDeviceName(newData, index)}
          onChangeData={() => getEditDeviceName(index)}
        />
      ),
      membership: (
        <ChoiceMembership
          devicesInfo={devicesInfo}
          defaultMembership={membership}
          membershipInfo={membershipInfo}
          index={index}
        />
      ),
      paymentTerminal: (
        <SelectWrapper>
          <DefaultSelectMenu
            defaultValue={isPaymentTerminal}
            options={paymentTerminalOptions}
            onChange={(newData) => handleChangeIsTerminal(newData, index)}
          />
        </SelectWrapper>
      ),
      terminalId: (
        <EditData
          text={terminalid}
          setNewData={(newData) => handleChangeTerminalId(newData, index)}
          onChangeData={() => getEditTerminalId(index)}
        />
      ),
      printer: (
        <SelectWrapper>
          <DefaultSelectMenu
            defaultValue={isPrintReceipt}
            options={printReceiptOptions}
            onChange={(newData) => handleChangePrintReceipt(newData, index)}
          />
        </SelectWrapper>
      ),
      spec: (
        <Popover title={'사양'} content={specContent} trigger="click">
          <TableCenter style={{ cursor: 'pointer' }}>
            <IconEye />
          </TableCenter>
        </Popover>
      ),
      email: <p>{loginuser}</p>,
      date: <TableCenter>{handleDate(logindate)}</TableCenter>,
      management: (
        <button onClick={() => handleClickMembershipDeleteButton(index)}>
          <IconDelete />
        </button>
      )
    };
  });

  return (
    <Wrapper>
      <h2>비즈멤버십 관리</h2>
      <p>인증된 장비의 이름을 수정하거나, 장비에 적용된 멤버십을 변경하실 수 있습니다.</p>
      <Table dataSource={dataSource} bordered columns={membershipInfoColumns} pagination={false} />
      {contextHolder}
      <InfoText>
        <li>
          디바이스에서 신규로 인증을 진행하신 경우 새로고침을 해야 지정하신 장비이름이 보입니다.
        </li>
        <li>
          장비에 설정할 수 있는 멤버십이 없는 경우, 멤버십구독 메뉴에서 멤버십을 먼저 구매하여
          주세요.
        </li>
        <li>X버튼을 누르실 경우 해당 장비가 삭제 됩니다.</li>
      </InfoText>
      <RefreshWrapper>
        <button onClick={() => window.location.reload()}>
          <IconRefresh />
          새로고침
        </button>
        <p>변경된 내용이 보이지 않을 경우 페이지를 새로고침해주세요.</p>
      </RefreshWrapper>
    </Wrapper>
  );
};

export default MembershipTable;

const Wrapper = styled.section`
  .ant-table-wrapper .ant-table-tbody td {
    padding: 16px 12px;
    vertical-align: middle;
  }

  .ant-table-wrapper thead tr th:first-of-type {
    width: 220px;
  }

  .ant-select-selection-item {
    font-size: 14px;
  }

  & > p {
    padding: 16px 0 24px;
  }
`;

const InfoText = styled.ul`
  margin: 24px 0;

  li {
    color: ${color.gray500};
    ${font.caption1_regular};

    &::before {
      content: '•';
      margin-inline-end: 1ch;
      color: ${color.gray500};
    }
  }
`;

const RefreshWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 48px;

  button {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    height: 44px;
    padding: 20px;
    border-radius: 24px;
    background: ${color.black2};
    color: ${color.white2};
    text-align: center;
    ${font.body_medium};
  }

  p {
    ${font.body_medium};
  }
`;

const TableCenter = styled.div`
  text-align: center;
`;

const SelectWrapper = styled.div`
  & .ant-select {
    width: 96px;
  }
`;
